import { useEffect, useRef, useState } from "react";
import { Loader2, Mic, Plus, Sparkles, Square, Trash2, Wand2 } from "lucide-react";
import { arenaApi } from "./data/api";
import { WavRecorder } from "./data/recorder";
import type { ArenaConfig, ArenaSample, SampleResult } from "./data/types";
import { AudioPlayer } from "./AudioPlayer";

const pct = (v: number | null) => (v == null ? "—" : `${(v * 100).toFixed(0)}%`);
const ms = (v: number | null) => (v == null ? "—" : `${Math.round(v)} ms`);

function nwerClass(v: number | null): string {
  if (v == null) return "stt-muted";
  if (v <= 0.1) return "stt-good";
  if (v <= 0.3) return "stt-ok";
  return "stt-bad";
}

type QStatus = "queued" | "uploading" | "mixing" | "running" | "done" | "error";

interface QItem {
  key: string; // local id
  text: string;
  accent: string;
  source: string;
  blob: Blob;
  blobUrl: string;
  status: QStatus;
  sample?: ArenaSample;
  results?: SampleResult[];
  error?: string;
}

const STATUS_LABEL: Record<QStatus, string> = {
  queued: "Queued",
  uploading: "Uploading…",
  mixing: "Mixing noise…",
  running: "Benchmarking…",
  done: "Done",
  error: "Error",
};

const MAX_CONCURRENT = 10; // samples benchmarked at once

let _kc = 0;
const nextKey = () => `q${Date.now()}_${_kc++}`;

export function AddSample({
  config,
  createdBy,
  onBenchmarked,
}: {
  config: ArenaConfig | null;
  createdBy: string;
  onBenchmarked: () => void;
}) {
  const [mode, setMode] = useState<"generate" | "custom">("generate");
  const [text, setText] = useState("");
  const [accent, setAccent] = useState("american");
  const [addNoise, setAddNoise] = useState(true);
  const [generating, setGenerating] = useState(false);

  // current recording (compose area)
  const recorderRef = useRef<WavRecorder | null>(null);
  const [recState, setRecState] = useState<"idle" | "recording" | "recorded">("idle");
  const [blob, setBlob] = useState<Blob | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [seconds, setSeconds] = useState(0);
  const timerRef = useRef<number | null>(null);

  // batch
  const [queue, setQueue] = useState<QItem[]>([]);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const providerCount = config?.providers.length ?? 6;
  const pending = queue.filter((q) => q.status === "queued" || q.status === "error").length;

  // Revoke all object URLs on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
      recorderRef.current?.cancel();
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      queue.forEach((q) => URL.revokeObjectURL(q.blobUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function generate() {
    setGenerating(true);
    setError(null);
    try {
      const r = await arenaApi.generateSentence();
      setText(r.text);
    } catch (e) {
      setError(`Sentence generation failed: ${(e as Error).message}. Is OPENAI_API_KEY set in the Arena service?`);
    } finally {
      setGenerating(false);
    }
  }

  async function startRec() {
    setError(null);
    try {
      const rec = new WavRecorder();
      await rec.start();
      recorderRef.current = rec;
      setRecState("recording");
      setSeconds(0);
      timerRef.current = window.setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (e) {
      setError(`Microphone access failed: ${(e as Error).message}`);
    }
  }

  async function stopRec() {
    if (timerRef.current) window.clearInterval(timerRef.current);
    const b = await recorderRef.current!.stop();
    recorderRef.current = null;
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlob(b);
    setBlobUrl(URL.createObjectURL(b));
    setRecState("recorded");
  }

  function discardRecording() {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlob(null);
    setBlobUrl(null);
    setRecState("idle");
    setSeconds(0);
  }

  const canAdd = text.trim().length > 0 && blob != null && recState === "recorded";

  // Move the current recording into the batch and reset the compose area.
  function addToBatch() {
    if (!blob || !blobUrl) return;
    setQueue((q) => [
      ...q,
      {
        key: nextKey(),
        text: text.trim(),
        accent,
        source: mode === "generate" ? "llm" : "custom",
        blob,
        blobUrl, // ownership transferred — not revoked here
        status: "queued",
      },
    ]);
    // keep accent + mode; clear text + recording for the next take
    setText("");
    setBlob(null);
    setBlobUrl(null);
    setRecState("idle");
    setSeconds(0);
  }

  function removeItem(key: string) {
    setQueue((q) => {
      const it = q.find((x) => x.key === key);
      if (it) URL.revokeObjectURL(it.blobUrl);
      return q.filter((x) => x.key !== key);
    });
  }

  function patch(key: string, p: Partial<QItem>) {
    setQueue((q) => q.map((x) => (x.key === key ? { ...x, ...p } : x)));
  }

  async function processItem(it: QItem) {
    try {
      patch(it.key, { status: "uploading", error: undefined });
      const form = new FormData();
      form.append("file", it.blob, "recording.wav");
      form.append("text", it.text);
      form.append("accent", it.accent);
      form.append("source", it.source);
      form.append("created_by", createdBy);
      let s = await arenaApi.uploadRecording(form);
      if (addNoise) {
        patch(it.key, { status: "mixing", sample: s });
        s = await arenaApi.addNoise(s.id);
      }
      patch(it.key, { status: "running", sample: s });
      const r = await arenaApi.run(s.id);
      patch(it.key, { status: "done", sample: s, results: r.results ?? [] });
    } catch (e) {
      patch(it.key, { status: "error", error: (e as Error).message });
    }
  }

  async function runAll() {
    setProcessing(true);
    setError(null);
    // snapshot the items to process (queued or previously errored)
    const todo = queue.filter((q) => q.status === "queued" || q.status === "error");
    // Worker pool: up to MAX_CONCURRENT samples benchmarked at once.
    let next = 0;
    const worker = async () => {
      while (next < todo.length) {
        const it = todo[next++];
        await processItem(it);
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(MAX_CONCURRENT, todo.length) }, () => worker()),
    );
    setProcessing(false);
    onBenchmarked();
  }

  return (
    <div className="stt-add">
      <div className="stt-add-grid">
        {/* ── Compose ─────────────────────────────────────────── */}
        <section className="stt-panel">
          <h3 className="stt-panel-title">1 · Script</h3>
          <div className="stt-segment stt-segment--ghost stt-mb">
            <button className={mode === "generate" ? "active" : ""} onClick={() => setMode("generate")}>
              <Sparkles size={13} /> Generate
            </button>
            <button className={mode === "custom" ? "active" : ""} onClick={() => setMode("custom")}>
              Custom text
            </button>
          </div>
          {mode === "generate" ? (
            <button className="stt-btn stt-btn--soft stt-mb" onClick={() => void generate()} disabled={generating}>
              {generating ? <Loader2 className="stt-spin" size={14} /> : <Wand2 size={14} />}
              {generating ? "Generating…" : "Generate a student utterance"}
            </button>
          ) : null}
          <textarea
            className="stt-textarea"
            placeholder="What the student says aloud — e.g. 'um so two x plus five equals thirteen, so x is four'"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
          />
          <label className="stt-field-label">Accent</label>
          <select className="stt-select stt-w-full" value={accent} onChange={(e) => setAccent(e.target.value)}>
            {config?.accents.map((a) => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
          </select>
        </section>

        {/* ── Record ──────────────────────────────────────────── */}
        <section className="stt-panel">
          <h3 className="stt-panel-title">2 · Record your voice</h3>
          <div className="stt-record-box">
            {recState === "idle" ? (
              <button className="stt-mic-btn" onClick={() => void startRec()}>
                <Mic size={18} /> Start recording
              </button>
            ) : recState === "recording" ? (
              <button className="stt-mic-btn stt-mic-btn--rec" onClick={() => void stopRec()}>
                <Square size={16} /> Stop · {seconds}s
                <span className="stt-rec-pulse" />
              </button>
            ) : (
              <div className="stt-rec-done">
                {blobUrl ? <AudioPlayer uri={blobUrl} tag="You" /> : null}
                <button className="stt-icon-btn" title="Discard" onClick={discardRecording}>
                  <Trash2 size={14} /> Redo
                </button>
              </div>
            )}
          </div>
          <button className="stt-btn stt-btn--soft" disabled={!canAdd} onClick={addToBatch}>
            <Plus size={15} /> Add to batch
          </button>
          <label className="stt-check stt-mt">
            <input type="checkbox" checked={addNoise} onChange={(e) => setAddNoise(e.target.checked)} />
            Add background noise — clean + {config?.snr_levels.join(" / ") ?? "5 / 10 / 15"} dB (real tutor-room bed)
          </label>
        </section>
      </div>

      {error ? <div className="stt-empty stt-empty--err stt-mt">{error}</div> : null}

      {/* ── Batch ─────────────────────────────────────────────── */}
      {queue.length ? (
        <section className="stt-panel stt-mt">
          <div className="stt-batch-head">
            <h3 className="stt-panel-title" style={{ margin: 0 }}>
              Batch · {queue.length} sample{queue.length > 1 ? "s" : ""}
            </h3>
            <button
              className="stt-btn stt-btn--primary"
              disabled={processing || pending === 0}
              onClick={() => void runAll()}
            >
              {processing ? <Loader2 className="stt-spin" size={15} /> : null}
              {processing
                ? "Running batch…"
                : `Run benchmark on ${pending} sample${pending > 1 ? "s" : ""} · ${providerCount} models`}
            </button>
          </div>

          <div className="stt-queue">
            {queue.map((it, i) => (
              <QueueRow key={it.key} item={it} index={i} onRemove={removeItem} busy={processing} />
            ))}
          </div>
        </section>
      ) : (
        <div className="stt-hint stt-mt">
          Record a clip and hit <b>Add to batch</b>. Repeat for as many as you like, then run them all together.
        </div>
      )}
    </div>
  );
}

function QueueRow({
  item,
  index,
  onRemove,
  busy,
}: {
  item: QItem;
  index: number;
  onRemove: (key: string) => void;
  busy: boolean;
}) {
  const active = item.status !== "queued" && item.status !== "done" && item.status !== "error";
  return (
    <div className="stt-qitem">
      <div className="stt-qhead">
        <span className="stt-qnum">{index + 1}</span>
        <span className="stt-badge">{item.accent}</span>
        <span className="stt-qtext" title={item.text}>{item.text}</span>
        <span className={`stt-qstatus stt-qstatus--${item.status}`}>
          {active ? <Loader2 className="stt-spin" size={12} /> : null}
          {STATUS_LABEL[item.status]}
        </span>
        {item.status === "queued" && !busy ? (
          <button className="stt-icon-btn" title="Remove" onClick={() => onRemove(item.key)}>
            <Trash2 size={13} />
          </button>
        ) : null}
      </div>

      {item.status === "error" ? <div className="stt-qerr">{item.error}</div> : null}

      {item.sample && (item.status === "running" || item.status === "done") ? (
        <div className="stt-preview-grid stt-mt">
          <AudioPlayer uri={item.sample.cleanUri} tag="Clean" compact />
          {item.sample.variants.map((v) => (
            <AudioPlayer key={v.uri} uri={v.uri} tag={`${v.snr_db} dB`} compact />
          ))}
        </div>
      ) : null}

      {item.status === "done" && item.results?.length ? <ResultsTable results={item.results} /> : null}
    </div>
  );
}

function ResultsTable({ results }: { results: SampleResult[] }) {
  const snrOf = (c: string) => (c === "clean" ? -1 : Number(c.replace("noisy_snr", "")) || 0);
  const conditions = Array.from(new Set(results.map((r) => r.condition))).sort(
    (a, b) => snrOf(a) - snrOf(b),
  );
  const models = Array.from(new Set(results.map((r) => `${r.provider}:${r.model}`)));
  const cell = (model: string, cond: string) =>
    results.find((r) => `${r.provider}:${r.model}` === model && r.condition === cond);

  return (
    <div className="stt-table-wrap stt-mt">
      <table className="stt-table stt-matrix">
        <thead>
          <tr>
            <th>Model</th>
            {conditions.map((c) => (
              <th key={c} className="num stt-cap">
                {c.replace("noisy_snr", "")}{c === "clean" ? "" : " dB"}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {models.map((m) => (
            <tr key={m}>
              <td>{m}</td>
              {conditions.map((c) => {
                const r = cell(m, c);
                return (
                  <td key={c} className="num">
                    {r ? (
                      <span className={nwerClass(r.nwer)} title={r.hypothesis ?? ""}>
                        {pct(r.nwer)}
                        <span className="stt-ttft">{ms(r.ttft_ms)}</span>
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
